<template>
  <div>
    <h2 class="mb-2 text-xl">{{ wtSummary('download', 'title') }}</h2>
    <HelpText slug="project_summary_download" />
    <p class="mb-6 text-sm text-neutral-450">
      {{ description }}
    </p>
    <div v-if="lastCalculatedAt || showGlobalLastDownloadedAt" class="mb-6 text-sm text-neutral-450 space-y-1">
      <p v-if="lastCalculatedAt">
        {{ t('project.summary.last_calculated') }}: {{ formatTimestampWithTime(lastCalculatedAt) }}
      </p>
      <!-- With stored states each list entry shows its own download date -->
      <p v-if="showGlobalLastDownloadedAt">
        {{ t('project.summary.last_downloaded') }}: {{ formatTimestampWithTime(lastDownloadedAt!) }}
      </p>
    </div>
    <button
      class="h-11 px-6 inline-flex items-center gap-2 core-action-button core-action-button--primary"
      :disabled="saving"
      data-testid="project-save-state"
      @click="$emit('downloadProject')">
      <component :is="canStorePdfs ? IconDeviceFloppy : IconDownload" size="18" stroke="2" />
      <span>{{ buttonLabel }}</span>
    </button>

    <div v-if="entries.length > 0" class="mt-10">
      <h3 class="mb-3 text-base">
        {{ wtSummary('download', 'statesListTitle') || t('project.summary.state.list_title') }}
      </h3>
      <ul class="list-none p-0 m-0 divide-y divide-neutral-200 border-y border-neutral-200">
        <li
          v-for="entry in entries"
          :key="entry.key"
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3"
          data-testid="project-state-entry">
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-neutral-900" data-testid="project-state-title">
              {{ entryTitle(entry) }}
            </p>
            <!-- One line on wide screens; the second date wraps below when space runs out -->
            <dl class="core-state-dates">
              <div>
                <dt>{{ t('project.summary.state.saved_at') }}</dt>
                <dd>{{ formatTimestampWithTime(new Date(entry.createdAt).getTime()) }}</dd>
              </div>
              <div v-if="entry.pdf?.lastDownloadedAt">
                <dt>{{ t('project.summary.last_downloaded') }}</dt>
                <dd>{{ formatTimestampWithTime(new Date(entry.pdf.lastDownloadedAt).getTime()) }}</dd>
              </div>
            </dl>
          </div>
          <div class="flex flex-wrap items-center gap-2 shrink-0">
            <button
              v-if="entry.pdf"
              v-tooltip="tooltip(downloadLabel)"
              type="button"
              class="core-state-icon-button core-action-button core-action-button--supporting"
              :aria-label="downloadLabel"
              :disabled="downloadingId === entry.pdf.id"
              @click="$emit('downloadPdf', entry.pdf)">
              <IconDownload size="18" stroke="2" />
            </button>
            <button
              v-if="entry.stateId"
              v-tooltip="tooltip(restoringId === entry.stateId ? t('project.summary.state.restoring') : restoreLabel)"
              type="button"
              class="core-state-icon-button core-action-button core-action-button--supporting"
              :aria-label="restoreLabel"
              :disabled="restoringId !== null"
              data-testid="project-state-restore"
              @click="$emit('restoreState', entry)">
              <IconHistory size="18" stroke="2" />
            </button>
            <button
              v-if="entry.stateId"
              v-tooltip="tooltip(renameLabel)"
              type="button"
              class="core-state-icon-button core-action-button core-action-button--supporting"
              :aria-label="renameLabel"
              :disabled="busyId !== null"
              data-testid="project-state-rename"
              @click="$emit('renameState', entry)">
              <IconPencil size="18" stroke="2" />
            </button>
            <button
              v-if="entry.stateId"
              v-tooltip="tooltip(deleteLabel)"
              type="button"
              class="core-state-icon-button core-action-button core-action-button--supporting"
              :aria-label="deleteLabel"
              :disabled="busyId !== null"
              data-testid="project-state-delete"
              @click="$emit('deleteState', entry)">
              <IconTrash size="18" stroke="2" />
            </button>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { IconDeviceFloppy, IconDownload, IconHistory, IconPencil, IconTrash } from '@tabler/icons-vue'
import { useI18n } from 'vue-i18n'
import HelpText from '@/components/partials/display/HelpText.vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import { formatTimestampWithTime } from '@/helpers/format'
import type { ProjectStateEntry } from '@/helpers/project-state-entries'

const { t } = useI18n()
const { wt: wtSummary } = useWidgetText('widget-texts-project-summary')

const props = withDefaults(
  defineProps<{
    lastCalculatedAt?: number
    lastDownloadedAt?: number
    entries?: ProjectStateEntry[]
    canStorePdfs?: boolean
    saving?: boolean
    downloadingId?: string | null
    restoringId?: string | null
    /** Id of the state currently being renamed or deleted. */
    busyId?: string | null
  }>(),
  {
    lastCalculatedAt: undefined,
    lastDownloadedAt: undefined,
    entries: () => [],
    canStorePdfs: false,
    saving: false,
    downloadingId: null,
    restoringId: null,
    busyId: null,
  }
)

const showGlobalLastDownloadedAt = computed(() => Boolean(props.lastDownloadedAt) && !props.canStorePdfs)

const description = computed(() => {
  if (props.canStorePdfs) {
    return wtSummary('download', 'stateDescription') || t('project.summary.state.description')
  }
  return wtSummary('download', 'description')
})

const buttonLabel = computed(() => {
  if (props.canStorePdfs) {
    return wtSummary('download', 'saveStateLabel') || t('project.summary.state.save_label')
  }
  return wtSummary('download', 'label')
})

const downloadLabel = computed(
  () => wtSummary('download', 'listDownloadLabel') || t('project.summary.state.download_pdf')
)
const restoreLabel = computed(() => wtSummary('download', 'restoreLabel') || t('project.summary.state.restore'))
const renameLabel = computed(() => t('project.summary.state.rename'))
const deleteLabel = computed(() => t('project.summary.state.delete'))

function tooltip(text: string) {
  return { text, theme: { placement: 'top' } }
}

function entryTitle(entry: ProjectStateEntry): string {
  return entry.label || entry.pdf?.name || t('project.summary.state.default_label')
}

defineEmits<{
  (e: 'downloadProject'): void
  (e: 'downloadPdf', pdf: NonNullable<ProjectStateEntry['pdf']>): void
  (e: 'restoreState', entry: ProjectStateEntry): void
  (e: 'renameState', entry: ProjectStateEntry): void
  (e: 'deleteState', entry: ProjectStateEntry): void
}>()
</script>

<style lang="postcss" scoped>
.core-state-dates {
  @apply m-0 mt-1 flex flex-wrap gap-x-6 gap-y-0.5 text-xs;

  div {
    @apply flex items-baseline gap-1.5 whitespace-nowrap;
  }

  dt {
    @apply text-neutral-450;

    &::after {
      content: ':';
    }
  }

  dd {
    @apply m-0 tabular-nums text-neutral-700;
  }
}

.core-state-icon-button {
  @apply inline-flex h-9 w-9 shrink-0 items-center justify-center p-0;
}
</style>
